import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserPlus, Eye, EyeOff, Phone, Lock, Gift, CheckCircle2, Copy, Check, Share2, MessageSquare, User, Mail } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Register = () => {
    const [searchParams] = useSearchParams();
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [createdAccount, setCreatedAccount] = useState(null);
    const [referralInfo, setReferralInfo] = useState(null);
    const [linkCopied, setLinkCopied] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();
    const refCode = searchParams.get('ref') || '';

    // Once the account is created, fetch the referral payouts so we can show
    // the player exactly how much they (and their friend) will earn per invite.
    useEffect(() => {
        if (!createdAccount) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/user/referral`, { withCredentials: true });
                if (!cancelled) setReferralInfo(data);
            } catch {
                /* non-blocking — show the link without bonus numbers */
            }
        })();
        return () => { cancelled = true; };
    }, [createdAccount]);

    const inviteLink = createdAccount?.referral_code
        ? `${window.location.origin}/api/r/${createdAccount.referral_code}`
        : '';

    const copyInvite = async () => {
        try {
            await navigator.clipboard.writeText(inviteLink);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        } catch { /* clipboard unavailable */ }
    };

    const shareInvite = async () => {
        const text = `Join me on BETohalics — get $5 for adding the app to your home screen + $5 for turning on notifications:`;
        if (navigator.share) {
            try {
                await navigator.share({ title: 'BETohalics', text, url: inviteLink });
            } catch { /* user cancelled */ }
        } else {
            copyInvite();
        }
    };

    const smsInvite = () => {
        const text = `Join me on BETohalics — get $5 for adding to home screen + $5 for turning on notifications: ${inviteLink}`;
        window.location.href = `sms:?&body=${encodeURIComponent(text)}`;
    };

    const phoneDigits = (phone.match(/\d/g) || []).length;
    const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
    const canSubmit = name.trim().length >= 2 && phoneDigits >= 7 && emailValid && password.length >= 6;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (name.trim().length < 2) {
            setError('Please enter your name.');
            return;
        }
        if (phoneDigits < 7) {
            setError('Please enter a valid phone number (at least 7 digits).');
            return;
        }
        if (!emailValid) {
            setError('Please enter a real email address — we use it to reset your password.');
            return;
        }
        if (password.length < 6) {
            setError('Password must be at least 6 characters.');
            return;
        }

        setLoading(true);
        const result = await register({
            name: name.trim(),
            phone: phone.trim(),
            email: email.trim().toLowerCase(),
            password,
            referral_code: refCode || null,
        });

        if (result.success) {
            setCreatedAccount({
                email: result.data?.email,
                name: result.data?.name,
                referral_code: result.data?.referral_code,
                pending_approval: !!result.pending_approval,
            });
        } else {
            setError(result.error);
        }

        setLoading(false);
    };

    // Success screen
    if (createdAccount) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-[#050505]">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="w-full max-w-md"
                >
                    <div className="card-dark p-8 text-center">
                        {createdAccount.pending_approval ? (
                            <>
                                <div className="w-16 h-16 rounded-full bg-[#FF8C00]/20 border border-[#FF8C00]/50 mx-auto mb-4 flex items-center justify-center">
                                    <span className="text-3xl">⏳</span>
                                </div>
                                <h2 className="text-2xl font-black text-white mb-2">You're almost in!</h2>
                                <p className="text-[#A0AAB2] mb-6">
                                    Your account is waiting for admin approval. We'll text you the moment you're activated — usually within a few minutes.
                                </p>
                            </>
                        ) : (
                            <>
                                <CheckCircle2 className="w-16 h-16 text-[#39FF14] mx-auto mb-4" />
                                <h2 className="text-2xl font-black text-white mb-2">You're in!</h2>
                                <p className="text-[#A0AAB2] mb-6">Sign in next time with your phone number and password.</p>
                            </>
                        )}

                        {/* Refer-a-friend share card */}
                        {createdAccount.referral_code && referralInfo?.enabled !== false && (
                            <div
                                className="bg-gradient-to-br from-[#39FF14]/10 to-[#00FFFF]/10 border border-[#39FF14]/30 rounded-lg p-4 mb-6 text-left"
                                data-testid="post-signup-invite-card"
                            >
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                    <Gift className="w-5 h-5 text-[#39FF14]" />
                                    <p className="text-white font-bold">Invite friends, both win</p>
                                    {referralInfo?.boost_active && (
                                        <span
                                            className="ml-auto px-2 py-0.5 rounded-full bg-[#FF6B00]/20 text-[#FF6B00] text-[10px] font-bold uppercase tracking-wider animate-pulse"
                                            data-testid="boost-badge"
                                        >
                                            Limited Time
                                        </span>
                                    )}
                                </div>
                                {referralInfo?.referrer_bonus != null && referralInfo?.referee_bonus != null ? (
                                    <p className="text-xs text-[#A0AAB2] mb-3">
                                        {referralInfo.referee_bonus > 0 ? (
                                            <>
                                                Earn <span className="text-[#39FF14] font-bold">${referralInfo.referrer_bonus}</span> for every friend who signs up, and they get
                                                <span className="text-[#39FF14] font-bold"> ${referralInfo.referee_bonus}</span> on us.
                                            </>
                                        ) : (
                                            <>
                                                Earn <span className="text-[#39FF14] font-bold">${referralInfo.referrer_bonus} Free Play</span> for every friend who signs up{referralInfo?.load_threshold > 0 ? <> and loads <span className="text-white font-bold">${referralInfo.load_threshold}</span></> : null}.
                                                <span className="block text-[10px] text-[#A0AAB2] mt-0.5 italic">Free Play credits · 1× wagering before withdrawal.</span>
                                            </>
                                        )}
                                        {referralInfo.referee_bonus > 0 && referralInfo?.load_threshold > 0 && (
                                            <span className="block text-[#A0AAB2] mt-1">
                                                Bonuses unlock after your friend loads <span className="text-white font-bold">${referralInfo.load_threshold}</span>.
                                            </span>
                                        )}
                                        {referralInfo?.boost_active && referralInfo.boost_expires_at && (
                                            <span className="block text-[#FF6B00] mt-1">
                                                🔥 Boosted until {new Date(referralInfo.boost_expires_at).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} CT
                                            </span>
                                        )}
                                    </p>
                                ) : (
                                    <p className="text-xs text-[#A0AAB2] mb-3">Share your link — earn bonus credits when your friend loads up.</p>
                                )}
                                <div className="flex items-stretch gap-2 bg-[#0a0a0a] rounded-lg border border-white/10 p-2 mb-3">
                                    <span className="flex-1 text-[10px] text-[#A0AAB2] font-mono break-all self-center pl-2" data-testid="invite-link">
                                        {inviteLink}
                                    </span>
                                    <Button
                                        type="button"
                                        onClick={copyInvite}
                                        className="bg-[#FFD700] text-black hover:bg-[#F4C430] px-3 py-2 text-xs font-bold flex-shrink-0"
                                        data-testid="copy-invite-btn"
                                    >
                                        {linkCopied ? <><Check className="w-3.5 h-3.5 mr-1" />Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" />Copy</>}
                                    </Button>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <Button
                                        type="button"
                                        onClick={smsInvite}
                                        variant="outline"
                                        className="bg-transparent border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10 text-xs"
                                        data-testid="sms-invite-btn"
                                    >
                                        <MessageSquare className="w-4 h-4 mr-1.5" /> Text it
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={shareInvite}
                                        variant="outline"
                                        className="bg-transparent border-[#00FFFF]/40 text-[#00FFFF] hover:bg-[#00FFFF]/10 text-xs"
                                        data-testid="share-invite-btn"
                                    >
                                        <Share2 className="w-4 h-4 mr-1.5" /> Share
                                    </Button>
                                </div>
                            </div>
                        )}

                        <Button
                            onClick={() => {
                                if (createdAccount.pending_approval) {
                                    navigate('/login');
                                    return;
                                }
                                // Honor a `?next=` deep-link so shared invites
                                // (Street Dice room codes etc.) land the new
                                // player right at the intended URL.
                                const params = new URLSearchParams(window.location.search);
                                const next = params.get('next');
                                if (next && next.startsWith('/')) {
                                    navigate(next);
                                } else {
                                    navigate('/lobby');
                                }
                            }}
                            className="w-full btn-primary py-6 rounded-xl font-bold"
                            data-testid="register-continue"
                        >
                            {createdAccount.pending_approval ? 'Back to Sign In'
                                : (new URLSearchParams(window.location.search).get('next')
                                    ? 'Continue to Table'
                                    : 'Continue to Lobby')}
                        </Button>
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-[#050505]">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md"
            >
                <div className="card-dark p-8">
                    <div className="text-center mb-8">
                        <Link to="/" className="inline-block">
                            <img
                                src="/betohalics-logo.png"
                                alt="Betohalics — Real Games. Real People. Real Wins."
                                className="mx-auto w-auto max-h-24 object-contain"
                                style={{ filter: 'drop-shadow(0 0 24px rgba(255,215,0,0.3))' }}
                                data-testid="register-logo-img"
                            />
                        </Link>
                        <p className="text-[#A0AAB2] mt-2">Sign up in seconds</p>
                    </div>

                    {error && (
                        <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="register-error">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="space-y-2">
                            <Label htmlFor="name" className="text-white text-base">Full Name</Label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AAB2]" />
                                <Input
                                    id="name"
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="John Smith"
                                    autoComplete="name"
                                    className="pl-10 py-6 text-base bg-[#121212] border-white/10 text-white placeholder:text-[#A0AAB2]/50 focus:border-[#FFD700] focus:ring-[#FFD700]/20"
                                    required
                                    data-testid="register-name"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="phone" className="text-white text-base">Phone Number</Label>
                            <div className="relative">
                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AAB2]" />
                                <Input
                                    id="phone"
                                    type="tel"
                                    inputMode="tel"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder="(555) 123-4567"
                                    autoComplete="tel"
                                    className="pl-10 py-6 text-base bg-[#121212] border-white/10 text-white placeholder:text-[#A0AAB2]/50 focus:border-[#FFD700] focus:ring-[#FFD700]/20"
                                    required
                                    data-testid="register-phone"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-white text-base">Email</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AAB2]" />
                                <Input
                                    id="email"
                                    type="email"
                                    inputMode="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@email.com"
                                    autoComplete="email"
                                    className="pl-10 py-6 text-base bg-[#121212] border-white/10 text-white placeholder:text-[#A0AAB2]/50 focus:border-[#FFD700] focus:ring-[#FFD700]/20"
                                    required
                                    data-testid="register-email"
                                />
                            </div>
                            <p className="text-xs text-[#A0AAB2]">Use a real inbox — it's how you'll reset a lost password.</p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-white text-base">Password</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AAB2]" />
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="At least 6 characters"
                                    autoComplete="new-password"
                                    className="pl-10 pr-10 py-6 text-base bg-[#121212] border-white/10 text-white placeholder:text-[#A0AAB2]/50 focus:border-[#FFD700] focus:ring-[#FFD700]/20"
                                    required
                                    data-testid="register-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0AAB2] hover:text-white transition-colors"
                                    aria-label="Toggle password visibility"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                            <p className="text-xs text-[#A0AAB2]">Quick &amp; simple — name, phone, password.</p>
                        </div>

                        {refCode && (
                            <div className="flex items-center gap-2 p-3 rounded-lg bg-[#39FF14]/10 border border-[#39FF14]/20" data-testid="referral-badge">
                                <Gift className="w-4 h-4 text-[#39FF14]" />
                                <span className="text-[#39FF14] text-sm">Referral code <span className="font-mono font-bold">{refCode}</span> applied! You'll get bonus credits.</span>
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={loading || !canSubmit}
                            className="w-full btn-primary py-6 rounded-xl font-bold text-base"
                            data-testid="register-submit"
                        >
                            {loading ? (
                                <span className="flex items-center justify-center">
                                    <span className="spinner mr-2" />
                                    Creating account...
                                </span>
                            ) : (
                                <span className="flex items-center justify-center">
                                    <UserPlus className="mr-2 w-5 h-5" />
                                    Create Account
                                </span>
                            )}
                        </Button>
                    </form>

                    <div className="mt-6 text-center">
                        <p className="text-[#A0AAB2]">
                            Already have an account?{' '}
                            <Link
                                to={`/login${window.location.search || ''}`}
                                className="text-[#FFD700] hover:text-[#F4C430] font-semibold"
                                data-testid="login-link"
                            >
                                Sign In
                            </Link>
                        </p>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default Register;
